import { createSlice } from "@reduxjs/toolkit";
// api
import { getPopoverTasksAPI, getTaskByIdAPI } from "src/api";
// utils
import { downloadURL } from "src/utils/handle-task";
import { batch } from "src/redux/store";

// ----------------------------------------------------------------------

const initialState = {
    isLoading: false,
    error: null,
    // ----------------------------------
    tasksPopover: [],
    //
    tasks: [],
    taskFilter: {
        biz_type: "all",
        start_day: "",
        end_day: "",
        page: 1,
        page_size: 10,
        total: 0
    },
    totalUnFinished: 0,
    //
    needRefreshOrder: false
};

const slice = createSlice({
    name: "task",
    initialState,
    reducers: {
        startLoading(state) {
            state.isLoading = true;
        },

        stopLoading(state) {
            state.isLoading = false;
        },

        hasError(state, action) {
            state.isLoading = false;
            //
            state.error = action.payload;
        },

        clearError(state) {
            state.error = null;
        },

        updateTaskListFilter(state, action) {
            state.taskFilter = {
                ...state.taskFilter,
                ...action.payload
            };
        },

        resetTaskListFilter(state) {
            state.taskFilter = {
                ...state.taskFilter,
                //
                biz_type: "all",
                start_day: "",
                end_day: "",
                page: 1
            };
        },

        // GET TASK POPOVER LIST
        getTasksPopoverSuccess(state, action) {
            state.tasksPopover = action.payload;
        },

        // GET TOTAL UNREAD MESSAGE NUMBER
        getTaskUnFinishedCountSuccess(state, action) {
            state.totalUnFinished = action.payload;
        },

        updateOrderRefreshStatus(state, action) {
            state.needRefreshOrder = action.payload;
        }
    }
});

// Reducer
export default slice.reducer;

// Actions
export const {
    getTaskUnFinishedCountSuccess,
    //
    updateOrderRefreshStatus,
    //
    updateTaskListFilter,
    resetTaskListFilter,
    //
    clearError
} = slice.actions;

// ----------------------------------------------------------------------

export function getUnreadTaskNum() {
    return async (dispatch) => {
        try {
            const { status, data } = await getPopoverTasksAPI({
                status: "PROCESSING",
                page: 1,
                page_size: 1
            });

            if (status === 200) {
                dispatch(slice.actions.getTaskUnFinishedCountSuccess(data?.data?.count ?? 0));
            }
        } catch (e) {
            dispatch(slice.actions.hasError(e));
        }
    };
}

// ----------------------------------------------------------------------

export function getTaskPopoverList() {
    return async (dispatch) => {
        try {
            const { status, data } = await getPopoverTasksAPI({
                page: 1,
                page_size: 3
            });

            if (status === 200 && data?.code === "ok") {
                dispatch(slice.actions.getTasksPopoverSuccess(data?.data?.tasks ?? []));
            }
        } catch (e) {
            dispatch(slice.actions.hasError(e));
        }
    };
}

// ----------------------------------------------------------------------

export function checkTaskById(ids) {
    return async (dispatch) => {
        try {
            const { status, data } = await getTaskByIdAPI(ids);

            return ( status === 200 && data?.code === "ok" ) ? ( data?.data ?? {} ) : {};
        } catch (e) {
            dispatch(slice.actions.hasError(e));

            return {};
        }
    };
}

// ----------------------------------------------------------------------

export function recursiveCheckTaskById(id, count, snackbar) {
    return async (dispatch) => {
        try {
            const result = await dispatch(checkTaskById([ id ]));

            const task = result[ id ];

            if (task) {
                batch(() => {
                    if (task?.status === "SUCCESS") {
                        dispatch(slice.actions.getTaskUnFinishedCountSuccess(0));

                        if (snackbar) {
                            const type = task.biz_type;

                            switch (type) {
                                case "ORDER_SYNC":
                                    snackbar("Sync Order success！The list is currently updated", { variant: "success" });
                                    break;
                                case "ORDER_IMPORT":
                                    snackbar("Order Import success！", { variant: "success" });
                                    break;
                                case "BATCH_DOWNLOAD_PACKING_SLIP":
                                    snackbar("Packing Slip download success！", { variant: "success" });
                                    break;
                                case "TRANSACTION_RECORD_EXPORT":
                                    snackbar("Transaction Record Export success！You can see it in the download list", { variant: "success" });
                                    break;
                                case "SHIPMENT_RECORD_EXPORT":
                                    snackbar("Shipment Record Export success！You can see it in the download list", { variant: "success" });
                                    break;
                                case "APPLY_SHIPPING_RULE":
                                    snackbar("Apply Shipping Rule success！", { variant: "success" });
                                    break;
                                default:
                                    break;
                            }
                        }

                        if (task?.export_url) {
                            downloadURL(task.export_url);
                        }
                    } else if (task?.status === "FAILED") {
                        if (snackbar) {
                            const type = task.biz_type;

                            switch (type) {
                                case "ORDER_SYNC":
                                    snackbar(`Sync Order failure！ ${ task?.fail_reason }`, { variant: "error" });
                                    break;
                                case "ORDER_IMPORT":
                                    snackbar(`Order Import failure！ ${ task?.fail_reason }`, { variant: "error" });
                                    break;
                                case "BATCH_DOWNLOAD_PACKING_SLIP":
                                    snackbar(`Packing Slip download failure！ ${ task?.fail_reason }`, { variant: "error" });
                                    break;
                                case "TRANSACTION_RECORD_EXPORT":
                                    snackbar(`Transaction Record Export failure！ ${ task?.fail_reason }`, { variant: "error" });
                                    break;
                                case "SHIPMENT_RECORD_EXPORT":
                                    snackbar(`Shipment Record Export failure！ ${ task?.fail_reason }`, { variant: "error" });
                                    break;
                                case "APPLY_SHIPPING_RULE":
                                    snackbar(`Apply Shipping Rule failure！ ${ task?.fail_reason }`, { variant: "error" });
                                    break;
                                default:
                                    break;
                            }
                        }

                        dispatch(slice.actions.getTaskUnFinishedCountSuccess(0));
                    } else if (task?.status === "PROCESSING") {
                        if (count < 51) {
                            dispatch(slice.actions.getTaskUnFinishedCountSuccess(1));

                            setTimeout(() => {
                                dispatch(recursiveCheckTaskById(id, count + 1, snackbar));
                            }, 2000);
                        } else {
                            // 超过设置检测次数
                        }
                    }
                });
            }
        } catch (e) {
            dispatch(slice.actions.hasError(e));
        }
    };
}
